import React, { useState, useEffect } from 'react'
import * as id from '../reportId'
import { Item } from '../../../common/snippets'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import moment from 'moment'
import { Box, Button } from '@mui/material'
import Datepicker from '../../../common/datePicker'
import DropDownOA from '../../../common/dropDownOA'
import { ReportHeading } from '../common/ReportHeading'
import { openReportInNewTab } from '../DispatchReport/DispatchReportUtils'
import { cartageSummaryColumns } from './Constant'
import { useSetAtom } from 'jotai'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import { urls } from '../../../common/constants'
import { GetRequest } from '../../../common/api'
import useToken from '../../../helpers/useTokens'
import { useSearchParams } from 'react-router-dom'
import { downloadPDF } from '../../Reports/analysisReport/pdfAutotable'
import { getDecryptedReportParams } from '../../../common/loginDecode'
import { focusElemnt } from '../../../common/helper'

const toNumber = (value) => Number(value) || 0
const round2 = (value) => Number(toNumber(value).toFixed(2))

const ALL_BRANCHES = { BranchID: 0, BranchName: 'All Branches' }

const CartageSummaryReport = () => {
    const setAlert = useSetAtom(alertAtom)
    const setLoader = useSetAtom(globalLoaderAtom)
    const reportName = 'Cartage Summary'
    const [fromDate, setFromDate] = useState(moment(new Date()))
    const [toDate, setToDate] = useState(moment(new Date()))
    const [branchData, setBranchData] = useState([ALL_BRANCHES])
    const [selectedBranch, setSelectedBranch] = useState(ALL_BRANCHES)
    const [urlSearchParams] = useSearchParams()
    const searchParams = getDecryptedReportParams(urlSearchParams)

    let { loginData = {} } = useToken()
    let isFromUrl = false
    if (!loginData) {
        loginData = {}
        loginData.intCompanyID = +searchParams.get('C') || 0
        loginData.intUserID = +searchParams.get('U') || 0
        loginData.intBranchID = +searchParams.get('B') || 0
        loginData.strUserName = searchParams.get('UN') || ''
        loginData.strCompanyName = searchParams.get('CN') || ''
        loginData.strBranchName = searchParams.get('BN') || ''
        isFromUrl = true
    }
    const { intCompanyID } = loginData

    const getRequestBody = (mode, branchId = 0, dateFrom = '', dateTo = '') => ({
        intCompanyID: intCompanyID,
        DateFrom: dateFrom,
        DateTo: dateTo,
        intBranchID: branchId,
        Mode: mode,
    })

    const criteriaHeaders = [
        { header: 'From Date', exportHead: 'From Date', field: 'From_Date' },
        { header: 'To Date', exportHead: 'To Date', field: 'To_Date' },
        { header: 'Branch Name', exportHead: 'Branch Name', field: 'Branch_Name' },
    ]

    const buildCriteriaData = (branchName) => [
        {
            dataType: 'entry',
            From_Date: moment(fromDate).format('DD-MM-YYYY'),
            To_Date: moment(toDate).format('DD-MM-YYYY'),
            Branch_Name: branchName || 'All Branches',
        },
    ]

    useEffect(() => {
        focusElemnt(id.fromDate)
    }, [])

    useEffect(() => {
        if (!intCompanyID) return
        const today = moment(new Date()).format('YYYY-MM-DD')
        GetRequest(urls.getBookingCartageSummaryReport, getRequestBody(0, 0, today, today))
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Failed to load branches')
                }
                const branches = res?.Data?.Table || []
                setBranchData([ALL_BRANCHES, ...branches])
            })
            .catch((err) => {
                setBranchData([ALL_BRANCHES])
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intCompanyID])

    const formatTableData = (table = []) => {
        const totals = {
            PickupCartage: 0,
            DropCartage: 0,
            DeliveryCartage: 0,
            TotalAmount: 0,
        }

        const rows = table.map((item, index) => {
            const row = {
                SN: index + 1,
                BranchName: item.BranchName || '',
                CityName: item.CityName || '',
                PickupCartage: toNumber(item.PickUpCartage),
                DropCartage: toNumber(item.DropCartage),
                DeliveryCartage: toNumber(item.DeliveryCartgae),
                TotalAmount: toNumber(item.TotalAmount),
                dataType: 'entry',
            }

            totals.PickupCartage += row.PickupCartage
            totals.DropCartage += row.DropCartage
            totals.DeliveryCartage += row.DeliveryCartage
            totals.TotalAmount += row.TotalAmount

            return row
        })

        if (rows.length > 0) {
            rows.push({
                SN: 'TOTAL',
                BranchName: '-',
                CityName: '-',
                PickupCartage: round2(totals.PickupCartage),
                DropCartage: round2(totals.DropCartage),
                DeliveryCartage: round2(totals.DeliveryCartage),
                TotalAmount: round2(totals.TotalAmount),
                dataType: 'total',
                colSpan: { SN: 3 },
                align: { SN: 'left' },
            })
        }

        return rows
    }

    const handleGo = (isPdf = false) => {
        if (Math.abs(toDate.diff(fromDate, 'days')) > 31) {
            setAlert({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' })
            return
        }
        const branchId = selectedBranch?.BranchID ?? 0
        const branchName = selectedBranch?.BranchName || 'All Branches'
        const dateFrom = moment(fromDate).format('YYYY-MM-DD')
        const dateTo = moment(toDate).format('YYYY-MM-DD')
        setLoader(true)
        GetRequest(urls.getBookingCartageSummaryReport, getRequestBody(1, branchId, dateFrom, dateTo))
            .then((res) => {
                if (res.status !== 'Success') {
                    throw new Error(res?.ErrMessage || 'Request Failed')
                }
                const tableData = res?.Data?.Table || []
                if (tableData.length === 0) {
                    throw new Error('No Data Found')
                }
                if (isPdf === true) {
                    downloadPDF({
                        reportName: 'Cartage Summary Report',
                        criteriaHeaders,
                        criteriaData: buildCriteriaData(branchName),
                        tableData: formatTableData(tableData),
                        visibleColumns: cartageSummaryColumns,
                        loginData,
                        showColumnBorders: true,
                    })
                    return
                }
                openReportInNewTab({
                    reportName: 'Cartage Summary Report',
                    mainHeader: cartageSummaryColumns,
                    criteriaHeaders,
                    criteriaData: buildCriteriaData(branchName),
                    tableData: formatTableData(tableData),
                    isShowManageColumn: false,
                    manageColumnID: '',
                    showColumnBorders: true,
                    isIncreFontSize: true,
                    serialNokey: 'SN',
                })
            })
            .catch((err) => {
                setAlert({ show: true, msg: err.message, nextId: '' })
            })
            .finally(() => setLoader(false))
    }

    const handleGoKeyDown = (e) => {
        if (e.key === 'Escape') focusElemnt(id.selBranch)
        if (e.key === 'Enter') {
            e.preventDefault()
            handleGo(isFromUrl)
        }
    }

    return (
        <>
            <Item sx={{ padding: '0' }} className="reports">
                {isFromUrl && <style>{`.global-header { display: none; } #root { zoom: 1.2; }`}</style>}
                {!isFromUrl && <ReportHeading reportName={reportName} />}
                <Box sx={{ ...sx.reportFlex, justifyContent: 'start', gap: isFromUrl ? '15px' : '10px' }}>
                    <Item sx={sx.itemsxGrow} style={{ width: isFromUrl ? '100%' : '90px' }} className="input--container bookingDatePicker">
                        <Datepicker
                            name={'From_Date'}
                            value={fromDate}
                            label={'From Date'}
                            id={id.fromDate}
                            dateChange={(date) => { setFromDate(date); focusElemnt(id.toDate) }}
                            width="100%"
                        />
                    </Item>
                    <Item
                        sx={sx.itemsxGrow} style={{ width: isFromUrl ? '100%' : '90px' }} className="input--container bookingDatePicker"
                        onKeyDownCapture={(e) => {
                            if (e.key !== 'Escape') return
                            e.preventDefault()
                            e.stopPropagation()
                            focusElemnt(id.fromDate)
                        }}
                    >
                        <Datepicker
                            label={'To Date'}
                            value={toDate}
                            id={id.toDate}
                            dateChange={(date) => { setToDate(date); focusElemnt(id.selBranch) }}
                            width="100%"
                        />
                    </Item>
                    <Item sx={sx.itemsxGrow} style={{ width: isFromUrl ? '100%' : '90px' }}>
                        <DropDownOA
                            id={id.selBranch}
                            label={'Branch Name'}
                            searchPlaceholder={`Search Branch`}
                            defaultvalue={selectedBranch?.BranchName || 'All Branches'}
                            data={branchData || []}
                            dataKey={`BranchName`}
                            idKey={`BranchID`}
                            onSelect={(obj) => setSelectedBranch(obj)}
                            onReset={() => setSelectedBranch(ALL_BRANCHES)}
                            prevId={id.toDate}
                            nextId={id.goButton}
                            width="100%"
                        />
                    </Item>
                    {isFromUrl ?
                        <Button variant="contained" sx={{ width: '100%', height: '40px' }} className="btn btn-primary"
                            onClick={() => handleGo(true)}
                            onKeyDown={handleGoKeyDown}
                            id={id.goButton}
                        >
                            DOWNLOAD PDF
                        </Button> :
                        <Item sx={sx.itemsxGrow} className="disabled-wrapper">
                            <Button id={id.goButton} variant="contained" style={{ ...sx.fullHeight, ...sx.novp }} className="btn btn-primary"
                                onClick={() => handleGo(false)}
                                onKeyDown={handleGoKeyDown}
                            >
                                GO
                            </Button>
                        </Item>
                    }
                </Box>
            </Item>
        </>
    )
}

export default CartageSummaryReport